import { Center, Group, Loader, Paper, Stack, Text } from "@mantine/core";
import { IconAlertTriangle, IconSparkles, IconTool } from "@tabler/icons-react";
import { getToolName, isToolUIPart } from "ai";
import type { UIMessage } from "ai";
import { memo } from "react";
import Markdown from "react-markdown";
import type { Rating } from "../../../../types/neWebApi.types";
import { ToolResultRenderer } from "../../ToolResultRenderer/ToolResultRenderer";
import type { ToolResultData } from "../../ToolResultRenderer/ToolResultRenderer.types";
import { MessageFeedback } from "./MessageFeedback/MessageFeedback";
import type { FeedbackState } from "./MessageFeedback/MessageFeedback.types";

interface ToolLabels {
  loading: string;
  failure: string;
}

const TOOL_LABELS: Record<string, ToolLabels> = {
  list_projects: {
    loading: "Projekte werden geladen...",
    failure: "Projekte laden"
  },
  get_project: { loading: "Projekt wird geladen...", failure: "Projekt laden" },
  list_sites: {
    loading: "Liegenschaften werden geladen...",
    failure: "Liegenschaften laden"
  },
  get_site: {
    loading: "Liegenschaft wird geladen...",
    failure: "Liegenschaft laden"
  },
  list_generators: {
    loading: "Erzeuger werden geladen...",
    failure: "Erzeuger laden"
  },
  list_mieterstrom_contracts: {
    loading: "Mieterstromverträge werden geladen...",
    failure: "Mieterstromverträge laden"
  },
  list_invoices: {
    loading: "Rechnungen werden geladen...",
    failure: "Rechnungen laden"
  },
  list_credit_notes: {
    loading: "Gutschriften werden geladen...",
    failure: "Gutschriften laden"
  },
  get_credit_note: {
    loading: "Gutschrift wird geladen...",
    failure: "Gutschrift laden"
  },
  list_blocked_credit_notes: {
    loading: "Blockierte Gutschriften werden geprüft...",
    failure: "Blockierte Gutschriften prüfen"
  },
  credit_note_total: {
    loading: "Gesamtbetrag wird berechnet...",
    failure: "Gesamtbetrag berechnen"
  },
  credit_note_monthly_breakdown: {
    loading: "Monatliche Aufschlüsselung wird berechnet...",
    failure: "Monatliche Aufschlüsselung berechnen"
  },
  search_entities: {
    loading: "Entitäten werden gesucht...",
    failure: "Entitäten suchen"
  },
  search_help: {
    loading: "Hilfe-Artikel werden gesucht...",
    failure: "Hilfe-Artikel suchen"
  },
  generator_feed_in_monthly: {
    loading: "Einspeisemengen werden berechnet...",
    failure: "Einspeisemengen berechnen"
  }
};

interface ChatMessageProps {
  feedbackState: FeedbackState | undefined;
  isAborted?: boolean;
  isTerminal: boolean;
  message: UIMessage;
  onFeedback: (messageId: string, rating: Rating, comment?: string) => void;
}

function ChatMessageComponent({
  feedbackState,
  isAborted = false,
  isTerminal,
  message,
  onFeedback
}: ChatMessageProps) {
  if (message.role === "user") {
    const text = message.parts
      .filter((p) => p.type === "text")
      .map((p) => (p as { type: "text"; text: string }).text)
      .join("");

    return (
      <Group justify="flex-end">
        <Paper
          bd="1px solid accent1.7"
          bg="accent1.0"
          maw="80%"
          mr="xs"
          p="sm"
          style={{ whiteSpace: "pre-wrap" }}
        >
          <Text size="sm">{text}</Text>
        </Paper>
      </Group>
    );
  }

  const lastToolPartIndex = message.parts.map(isToolUIPart).lastIndexOf(true);

  return (
    <Group align="flex-start" gap="xs" wrap="nowrap">
      <Center
        h="calc(var(--mantine-font-size-sm) * var(--mantine-line-height))"
        style={{ flexShrink: 0 }}
      >
        <IconSparkles color="var(--mantine-color-accent1-7)" size={12} />
      </Center>
      <Stack fz="sm" gap="xs" maw="90%">
        {isAborted ? (
          <Text c="dimmed" fs="italic" size="xs">
            Anfrage abgebrochen.
          </Text>
        ) : null}
        {message.parts.map((part, i) => {
          if (part.type === "text") {
            // Text before a tool call is the model's progress note, not the answer
            if (i < lastToolPartIndex) {
              return (
                <Text c="dimmed" component="div" key={i} size="xs">
                  <Markdown>{part.text}</Markdown>
                </Text>
              );
            }

            return <Markdown key={i}>{part.text}</Markdown>;
          }

          if (isToolUIPart(part)) {
            const toolName = getToolName(part);

            if (part.state === "output-available") {
              return (
                <ToolResultRenderer
                  data={part.output as ToolResultData}
                  key={part.toolCallId}
                />
              );
            }

            if (part.state === "output-error") {
              return (
                <Group gap="xs" key={part.toolCallId} wrap="nowrap">
                  <IconAlertTriangle
                    color="var(--mantine-color-error-filled)"
                    size="var(--mantine-font-size-xs)"
                    style={{ flexShrink: 0 }}
                  />
                  <Text c="error" size="xs">
                    {TOOL_LABELS[toolName]?.failure ?? "Aktion"} fehlgeschlagen
                  </Text>
                </Group>
              );
            }

            return (
              <Group gap="xs" key={part.toolCallId}>
                <Loader size="xs" />
                <Text c="dimmed" size="xs">
                  <IconTool
                    size={12}
                    style={{ verticalAlign: "middle", marginRight: 4 }}
                  />
                  {TOOL_LABELS[toolName]?.loading ??
                    `${toolName} wird ausgeführt...`}
                </Text>
              </Group>
            );
          }

          return null;
        })}
        {isTerminal && (
          <MessageFeedback
            feedbackState={feedbackState}
            messageId={message.id}
            onSubmit={onFeedback}
          />
        )}
      </Stack>
    </Group>
  );
}

const ChatMessage = memo(ChatMessageComponent);

export { ChatMessage };
